import { Form, Formik } from 'formik';
import InputText from '../../../components/input-text/inputText';
import ModalComponent from '../../../components/modal/modal';
import ModalBody from '../../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import CustomTextArea from '../../../components/text-area/textArea';
import {
  // useGetFeature,
  useGetFeatureSetById,
  useGetFeaturesPermissionsDistributable,
  usePostEmployeeTypes,
  usePostFeatureSet,
} from '../../../hooks';
import { type ModalStatusType } from '../../../types/common';
import { FEATURE_SET_VALIDATION } from '../../../utils/validations/validations';
import _ from 'lodash';
import CustomCheckBox from '../../../components/radio-button/customCheckBox';
import {
  CheckBoxFlex,
  FieldLevelPermissionsGroups,
  ListWrapper,
} from '../system-roles/roles.styled';
import Tag from '../../../components/lozenge/tags';
import { HeadingMain } from '../../../constants/style/common.styled';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthRoutesList } from '../../configuration/routeSlice';
import Search from '../../../components/search/search';
import Heading from '../../../components/typography';
import {
  ROLES_PERMISSION_LIST,
  SORTED_FIELD_PERMISSION,
  SORTED_MODULE_PERMISSION,
} from '../../../constants/common';
import { handleFilterPermissionList, handleSortPermissionNames } from '../../../utils/global';
import {
  convertDualPermissions,
  convertPermissions,
} from '../../../utils/permission/permissionAdapter';
import useGetFeaturePermissionSet from '../../../hooks/get/useGetFeaturePermissionSet';
import { getFilteredPermissionData } from '../../../utils';

interface FeatureSetModalTypes {
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  setModalStatus: (value: ModalStatusType) => void;
  initialData: {
    data: any;
    id: string;
  };
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>;
  id?: string;
}

const FeatureSetModal = ({
  isModalOpen,
  closeModal,
  initialData,
  setModalStatus,
  modalStatus,
  handleRequest,
  id,
}: FeatureSetModalTypes): JSX.Element => {
  const { mutate, isLoading } = usePostFeatureSet(handleRequest, closeModal, initialData.id);
  const userAuthList = useSelector(selectAuthRoutesList);
  const allFieldValues = userAuthList.localization?.values?.Wealthlane;
  const [manualSearch, setManualSearch] = useState('');

  const { data: newPermissions } = useGetFeaturePermissionSet(handleRequest);
  const { data: distributablePermissions } = useGetFeaturesPermissionsDistributable(handleRequest);

  const [mergeDate, setMergeData] = useState<any>();

  useEffect(() => {
    if (distributablePermissions?.fieldLevelPermissions) {
      setMergeData(
        distributablePermissions?.permissions?.concat(
          distributablePermissions?.fieldLevelPermissions
        )
      );
    }
  }, [distributablePermissions]);

  const { nonFieldLevelPermissionsGroups, fieldLevelPermissionsGroups } = getFilteredPermissionData(
    convertDualPermissions(mergeDate ?? []),
    'CustomerService.Customer.',
    allFieldValues
  );
  const filterNonFieldLevelPermission = handleSortPermissionNames(
    handleFilterPermissionList(nonFieldLevelPermissionsGroups),
    SORTED_MODULE_PERMISSION
  );
  const filterFieldLevelPermission = handleSortPermissionNames(
    handleFilterPermissionList(fieldLevelPermissionsGroups),
    SORTED_FIELD_PERMISSION
  );

  const filteredData = Object.entries(filterNonFieldLevelPermission).reduce(
    (acc, [key, value]: any) => {
      const filteredItems = value.filter((item: any) =>
        item?.displayName?.toLowerCase().includes(manualSearch?.toLowerCase())
      );

      if (filteredItems.length > 0) {
        acc[key] = filteredItems;
      }
      return acc;
    },
    {} as typeof filterNonFieldLevelPermission
  );

  const filteredFieldLevelData = Object.entries(filterFieldLevelPermission).reduce(
    (acc, [key, value]: any) => {
      const filteredItems = value.filter((item: any) =>
        allFieldValues[item?.key]?.toLowerCase().includes(manualSearch?.toLowerCase())
      );
      if (filteredItems.length > 0) {
        acc[key] = filteredItems;
      }
      return acc;
    },
    {} as typeof filterFieldLevelPermission
  );

  const handleCheckedAll = (
    key: string,
    setFieldValue: any,
    dataList: any,
    wrapperName: string,
    isCheckedAll: boolean
  ): void => {
    const allFields = handleFilterPermissionList(dataList)[key];
    const filterAllDisabledData = allFields.filter(
      (item: any) => !(item.isDisabled as boolean) && item
    );

    if (isCheckedAll) {
      allFields.forEach((item: any) =>
        setFieldValue(`${wrapperName}[${String(item.key.replaceAll('.', '-') ?? '')}]`, false)
      );
    } else {
      filterAllDisabledData.forEach((item: any) =>
        setFieldValue(`${wrapperName}[${String(item.key.replaceAll('.', '-') ?? '')}]`, true)
      );
    }
  };

  const handleSubmit = (values: any): void => {
    const permissions = Object.keys(values?.permissions)
      .filter((x) => values.permissions[x] !== false)
      .map((item) => item.replaceAll('-', '.'));
    const fieldLevelPermissions = Object.keys(values?.fieldLevelPermissions)
      .filter((x) => values.fieldLevelPermissions[x] !== false)
      .map((item) => item.replaceAll('-', '.'));

    const payload = {
      ...values,
      permissions: permissions.concat(fieldLevelPermissions),
    };
    mutate(payload);
  };

  console.log(mergeDate);

  return (
    <Formik
      enableReinitialize
      initialValues={initialData.data}
      validationSchema={FEATURE_SET_VALIDATION}
      onSubmit={(values) => {
        handleSubmit(values);
      }}>
      {({ touched, handleChange, errors, values, setFieldValue }) => {
        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            // loading={loading}
            size='lg'
            title={`${modalStatus === 'Add' ? 'Create' : modalStatus} Feature Set`}
            valueEdited={_.isEqual(values, initialData.data)}
            id={id}
            children={
              <Form id="featureSetForm">
                <ModalBody id="featureSetModalBody">
                  <InputText
                    type='text'
                    label='Name'
                    name='name'
                    id='name'
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    inputType={modalStatus}
                    disabled={modalStatus === 'View'}
                    required
                  />
                  <CustomTextArea
                    label='Description'
                    rows='5'
                    name='description'
                    id='description'
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    disabled={modalStatus === 'View'}
                  />

                  <div id="searchSection">
                    <Search
                      placeholder='Search Module & Field Level Permissions'
                      searchValue={manualSearch}
                      onSearch={(value: string) => {
                        setManualSearch(value);
                      }}
                    />
                  </div>

                  <FieldLevelPermissionsGroups id="moduleLevelPermissionsSection">
                    <div className='grid grid-cols-12 gap-x-5 roleTitle'>
                      <div className='col-span-5'>
                        <Tag variant='info' title='Module Level Permission List' />
                      </div>
                      <div className='grid grid-cols-12 col-span-7'>
                        <Heading
                          variant='body-xs'
                          title='All'
                          className='col-span-2 roleSubTitle'
                        />
                        {ROLES_PERMISSION_LIST.map((col, i) => (
                          <Heading
                            key={`header-table-${i}`}
                            variant='body-xs'
                            title={col}
                            className='col-span-2 roleSubTitle'
                          />
                        ))}
                      </div>
                    </div>

                    {/* {Object.keys(filteredData)
                      .sort((a, b) => {
                        const keyA = filteredData[a][0]?.key || '';
                        const keyB = filteredData[b][0]?.key || '';

                        const valueA = allFieldValues[keyA] || '';
                        const valueB = allFieldValues[keyB] || '';

                        return valueA.localeCompare(valueB);
                      })
                      .map((data: string) => {
                        const allInfo = filteredData[data].filter(
                          (item: any) => !(item?.isDisabled as boolean) && item
                        );
                        const checkRoles = allInfo?.map(
                          (item: any) =>
                            !!(values?.permissions[item?.key.replaceAll('.', '-')] as boolean)
                        );
                        const labelKey = Boolean(allInfo) && allInfo?.length > 0 && allInfo[0].key; */}
                    {Object.keys(filteredData)
                      .sort((a, b) => {
                        const keyA = filteredData[a][0]?.key || '';
                        const keyB = filteredData[b][0]?.key || '';

                        const valueA = allFieldValues[keyA] || '';
                        const valueB = allFieldValues[keyB] || '';

                        return valueA.localeCompare(valueB);
                      })
                      .map((data: string) => {
                        const allInfo = filteredData[data].filter(
                          (item: any) => !(item.isDisabled as boolean) && item
                        );

                        const rolesInfo = filterNonFieldLevelPermission[data].filter(
                          (item: any) => !(item.isDisabled as boolean) && item
                        );

                        const checkRoles = rolesInfo.map(
                          (item: any) =>
                            !!(values.permissions[item.key.replaceAll('.', '-')] as boolean)
                        );

                        const labelKey = Boolean(allInfo) && allInfo.length > 0 && allInfo[0].key;

                        if (!allFieldValues[labelKey]) return;

                        return (
                          <ListWrapper className='grid items-center grid-cols-12 gap-x-5'>
                            <HeadingMain className='col-span-5'>
                              {(allFieldValues as boolean) ? allFieldValues[labelKey] : '-'}
                            </HeadingMain>
                            <CheckBoxFlex className='grid grid-cols-12 col-span-7'>
                              <div className='justify-center col-span-2 CheckboxCenterInRoles'>
                                <CustomCheckBox
                                  name={`nonFieldPermissions${data}All`}
                                  onChange={() => {
                                    handleCheckedAll(
                                      data,
                                      setFieldValue,
                                      nonFieldLevelPermissionsGroups,
                                      'permissions',
                                      checkRoles.every((v: boolean) => v)
                                    );
                                  }}
                                  errors={errors}
                                  touched={touched}
                                  disabled={modalStatus === 'View'}
                                  checked={checkRoles.every((v: boolean) => v)}
                                />
                              </div>
                              {ROLES_PERMISSION_LIST.map((item, index) => {
                                const itemData = handleFilterPermissionList(
                                  nonFieldLevelPermissionsGroups
                                )[`${data}`][index];
                                return (
                                  <div className='justify-center col-span-2 CheckboxCenterInRoles'>
                                    <CustomCheckBox
                                      name={`permissions[${String(
                                        itemData.key.replaceAll('.', '-') ?? ''
                                      )}]`}
                                      onChange={(e: any) => {
                                        if (
                                          !(itemData.key.includes('View') === true) &&
                                          e.target.checked === true
                                        ) {
                                          const newValue = itemData.key
                                            .replaceAll('.', '-')
                                            .split('-');

                                          const filterValue = newValue
                                            .filter(
                                              (item: string, index: number) =>
                                                index !== newValue.length - 1 && item
                                            )
                                            .join('-');
                                          setFieldValue(
                                            `permissions[${String(filterValue ?? '')}]`,
                                            true
                                          );
                                        }

                                        setFieldValue(
                                          `permissions[${String(
                                            itemData.key.replaceAll('.', '-') ?? ''
                                          )}]`,
                                          e.target.checked
                                        );
                                      }}
                                      errors={errors}
                                      touched={touched}
                                      id={itemData.key}
                                      arrValue={
                                        item === itemData.text &&
                                        values?.permissions[itemData.key.replaceAll('.', '-')]
                                      }
                                      disabled={
                                        modalStatus === 'View' ||
                                        !(itemData.key.length as boolean) ||
                                        itemData.isDisabled
                                      }
                                    />
                                  </div>
                                );
                              })}
                            </CheckBoxFlex>
                          </ListWrapper>
                        );
                      })}
                  </FieldLevelPermissionsGroups>
                  <FieldLevelPermissionsGroups id="fieldLevelPermissionsSection">
                    {Object.keys(filteredFieldLevelData).length > 0 && (
                      <div className='grid grid-cols-12 gap-x-5 roleTitle'>
                        <div className='col-span-5'>
                          <Tag variant='info' title='Field Level Permission' />
                        </div>
                      </div>
                    )}
                    {Object.keys(filteredFieldLevelData).map((data: string) => {
                      const allInfo = filteredFieldLevelData[data]?.filter(
                        (item: any) => !(item?.isDisabled as boolean) && item
                      );

                      const checkRoles = allInfo?.map(
                        (item: any) =>
                          !!(values?.fieldLevelPermissions[
                            item?.key?.replaceAll('.', '-')
                          ] as boolean)
                      );
                      const labelKey = Boolean(allInfo) && allInfo.length > 0 && allInfo[0].key;
                      return (
                        <ListWrapper className='grid items-center grid-cols-12 gap-x-5' id={`fieldLevelPermissionRow${data}`}>
                          <HeadingMain className='col-span-5'>
                            {(allFieldValues as boolean) ? allFieldValues[labelKey] : '-'}
                          </HeadingMain>
                          <CheckBoxFlex className='grid grid-cols-12 col-span-7'>
                            <div className='justify-center col-span-2 CheckboxCenterInRoles'>
                              <CustomCheckBox
                                name={`fieldLevelPermissions${data}All`}
                                id={`fieldLevelPermissions${data}All`}
                                onChange={() => {
                                  handleCheckedAll(
                                    data,
                                    setFieldValue,
                                    fieldLevelPermissionsGroups,
                                    'fieldLevelPermissions',
                                    checkRoles.every((v: boolean) => v)
                                  );
                                }}
                                errors={errors}
                                touched={touched}
                                disabled={modalStatus === 'View'}
                                checked={checkRoles.every((v: boolean) => v)}
                              />
                            </div>
                            {ROLES_PERMISSION_LIST.map((item, index) => {
                              const itemData = handleFilterPermissionList(
                                fieldLevelPermissionsGroups
                              )[`${data}`][index];
                              return (
                                <div className='justify-center col-span-2 CheckboxCenterInRoles'>
                                  <CustomCheckBox
                                    name={`fieldLevelPermissions[${String(
                                      itemData.key.replaceAll('.', '-') ?? ''
                                    )}]`}
                                    id={`fieldLevelPermissions[${String(
                                      itemData.key.replaceAll('.', '-') ?? ''
                                    )}]`}
                                    onChange={(e: any) => {
                                      if (
                                        !(itemData.key.includes('View') === true) &&
                                        e.target.checked === true
                                      ) {
                                        const newValue = itemData.key
                                          .replaceAll('.', '-')
                                          .split('-');

                                        const filterValue = newValue
                                          .filter(
                                            (item: string, index: number) =>
                                              index !== newValue.length - 1 && item
                                          )
                                          .join('-');
                                        setFieldValue(
                                          `fieldLevelPermissions[${String(filterValue ?? '')}]`,
                                          true
                                        );
                                      }

                                      setFieldValue(
                                        `fieldLevelPermissions[${String(
                                          itemData.key.replaceAll('.', '-') ?? ''
                                        )}]`,
                                        e.target.checked
                                      );
                                    }}
                                    errors={errors}
                                    touched={touched}
                                    arrValue={
                                      item === itemData.text &&
                                      values.fieldLevelPermissions[
                                      itemData.key.replaceAll('.', '-')
                                      ]
                                    }
                                    disabled={
                                      modalStatus === 'View' ||
                                      !(itemData.key.length as boolean) ||
                                      itemData.isDisabled
                                    }
                                  />
                                </div>
                              );
                            })}
                          </CheckBoxFlex>
                        </ListWrapper>
                      );
                    })}
                  </FieldLevelPermissionsGroups>
                </ModalBody>
                <ModalFooterComponent
                  id="featureSetModalFooter"
                  modalStatus={modalStatus}
                  onCloseModal={() => {
                    closeModal();
                  }}
                  isEditShow
                  onEditModal={() => {
                    setModalStatus('Edit');
                  }}
                  urlName='feature-set'
                  valueChanged={_.isEqual(values, initialData.data)}
                  loading={isLoading}
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default FeatureSetModal;
